import { ReadMoreHtml } from "@/components/read-more/ReadMoreHtml";
import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
  type Attachment as ApiAttachment,
} from "@/api/attachment";
import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import { Accordion } from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useGetInstanceGeneralSettings } from "@/api/user-settings";
import { useAuth } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import {
  DimensionsFormatting,
  hasDisplayableDimensions,
} from "@/lib/compute-field/shared/dimensions";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import {
  computeFieldCollectorNameForOverview,
  computeFieldRelatedAuctions,
  computeFieldRelatedAuthorsArtwork,
  computeFieldRelatedBibliographies,
  computeFieldRelatedExhibitions,
  computeFieldTirageHistory,
} from "@/lib/computeFields";
import { DimensionsDTO } from "@/lib/shared-types";
import { filterPublishedTirageInAuction } from "@/lib/tirage-auctions";
import { buildTirageProvenance } from "@/lib/tirage-provenance";
import {
  mapTirageInAuctionsToArtwork,
  mapTirageInBibliographiesToArtwork,
  mapTirageInExhibitionsToArtwork,
} from "@/lib/tirage-relation-mappers";
import { Tirage, TirageInAuction } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { useMemo, useState } from "react";
import { MergedDisplayData } from "../types";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { HistoryAccordion } from "./HistoryAccordion";

interface TirageDrawerProps {
  tirage: Tirage;
  displayData: MergedDisplayData;
  locale: SupportedLocale;
  t: (key: string) => string;
  trigger: React.ReactNode;
  isTirage: boolean;
  /** Attachments already loaded by the accordion row (avoids a second fetch). */
  preloadedAttachments?: ApiAttachment[];
}

export function TirageDrawer({
  tirage,
  displayData,
  locale,
  t,
  trigger,
  preloadedAttachments,
}: TirageDrawerProps) {
  const { instanceData } = useAuth();
  const { data: generalSettings } = useGetInstanceGeneralSettings();
  const provenanceMode = instanceData?.provenanceMode ?? false;
  const itinerantExhibitionGrouped =
    generalSettings?.itinerantExhibitionGrouped ??
    instanceData?.itinerantExhibitionGrouped ??
    false;
  // Gate fetches on sheet open — mounting every row used to subscribe N drawers at once.
  const [open, setOpen] = useState(false);

  const listAttachments =
    preloadedAttachments ??
    (tirage.attachments as ApiAttachment[] | undefined);
  const { data: fetchedAttachments } = useGetAttachmentsByDocId(
    open && !listAttachments?.length ? tirage.id : undefined,
  );
  const attachments = listAttachments?.length
    ? listAttachments
    : fetchedAttachments;

  const previewAttachments = useMemo(() => {
    if (!attachments?.length) return [];
    return attachments.filter((a) =>
      shouldIncludeAttachment(
        a,
        (tirage.tiragePublicationStatus ?? "draft") as "published" | "draft",
      ),
    );
  }, [attachments, tirage.tiragePublicationStatus]);
  const publishedTirageInAuction = useMemo(
    () =>
      filterPublishedTirageInAuction<TirageInAuction>(
        tirage.tirageInAuction ?? [],
      ),
    [tirage.tirageInAuction],
  );

  // Compute author from master artwork
  const author = useMemo(() => {
    return computeFieldRelatedAuthorsArtwork(displayData, {
      language: locale,
    });
  }, [displayData, locale]);

  // Parse dimensions
  const dimensionsWithMargins = useMemo(() => {
    if (!tirage?.tirageDescriptionDimensionsWithMargins) return null;
    return typeof tirage.tirageDescriptionDimensionsWithMargins === "string"
      ? parseJsonOrUndefined<DimensionsDTO>(
          tirage.tirageDescriptionDimensionsWithMargins,
        )
      : tirage.tirageDescriptionDimensionsWithMargins;
  }, [tirage?.tirageDescriptionDimensionsWithMargins]);

  const dimensionsWithoutMargins = useMemo(() => {
    if (!tirage?.tirageDescriptionDimensionsWithoutMargins) return null;
    return typeof tirage.tirageDescriptionDimensionsWithoutMargins === "string"
      ? parseJsonOrUndefined<DimensionsDTO>(
          tirage.tirageDescriptionDimensionsWithoutMargins,
        )
      : tirage.tirageDescriptionDimensionsWithoutMargins;
  }, [tirage?.tirageDescriptionDimensionsWithoutMargins]);

  // Des dimensions stockees mais vides (objet JSON sans aucune mesure) rendaient
  // quand meme le libelle suivi de rien (ticket #1055).
  const hasDimensionsWithMargins =
    hasDisplayableDimensions(dimensionsWithMargins);
  const hasDimensionsWithoutMargins = hasDisplayableDimensions(
    dimensionsWithoutMargins,
  );

  // Helper to get multilingual field value
  const getMultilingualField = (
    field: Record<SupportedLocale, string> | undefined,
  ): string | null => {
    if (!field) return null;
    return field[locale] || null;
  };

  const tirageHistoryCalculated = useMemo(() => {
    const hasHistory = (tirage.tirageHistory?.length ?? 0) > 0;
    if (provenanceMode) {
      if (!hasHistory && publishedTirageInAuction.length === 0) {
        return null;
      }
      return buildTirageProvenance({
        history: tirage.tirageHistory,
        auctions: publishedTirageInAuction,
        t,
      });
    }
    if (!hasHistory) {
      return null;
    }
    return computeFieldTirageHistory(
      { tirageHistory: tirage.tirageHistory ?? [] },
      { language: locale, translation: t, lineBreak: true },
    );
  }, [
    locale,
    provenanceMode,
    publishedTirageInAuction,
    t,
    tirage.tirageHistory,
  ]);

  // Compute fields for tirage-specific relations
  // Use centralized mappers for consistency and maintainability
  const tirageExhibitionsCalculated = useMemo(() => {
    const adaptedExhibitions = mapTirageInExhibitionsToArtwork(
      tirage.tirageInExhibition,
    );
    if (!adaptedExhibitions) {
      return null;
    }
    return computeFieldRelatedExhibitions(
      {
        relatedExhbitions: adaptedExhibitions,
        groupItinerantExhibitions: itinerantExhibitionGrouped,
      },
      { language: locale },
    );
  }, [tirage.tirageInExhibition, itinerantExhibitionGrouped, locale]);

  const tirageAuctionsCalculated = useMemo(() => {
    const adaptedAuctions = mapTirageInAuctionsToArtwork(
      publishedTirageInAuction,
    );
    if (!adaptedAuctions) {
      return null;
    }
    return computeFieldRelatedAuctions(
      { relatedAuctions: adaptedAuctions },
      { language: locale },
    );
  }, [publishedTirageInAuction, locale]);

  const tirageBibliographyCalculated = useMemo(() => {
    const adaptedBibliographies = mapTirageInBibliographiesToArtwork(
      tirage.tirageInBibliography,
    );
    if (!adaptedBibliographies) {
      return null;
    }
    return computeFieldRelatedBibliographies(
      { relatedBibliographies: adaptedBibliographies },
      { language: locale },
    );
  }, [tirage.tirageInBibliography, locale]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto pb-8"
      >
        <SheetHeader>
          <SheetTitle className="text-center">
            {tirage.tirageDescriptionNumeroEnregistrement}
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col items-center gap-4 mt-6">
          {tirage && (
            <>
              {/* Image */}
              {previewAttachments.length > 0 && (
                <div className="w-full max-w-md flex justify-center">
                  <ImageFullscreen
                    media={{
                      attachments: previewAttachments,
                      type: "tirage",
                      alt:
                        tirage.tirageDescriptionNumeroEnregistrement ||
                        "Tirage",
                      width: 600,
                      height: 600,
                      className: "object-contain max-h-[400px] w-auto",
                    }}
                  />
                </div>
              )}

              {/* Author */}
              {author && (
                <p className="font-bold text-[15px] text-center w-full">
                  {t("tirageDescriptionAuteurLabelFront") + " "}
                  <span dangerouslySetInnerHTML={{ __html: author }} />
                </p>
              )}

              {/* Registration number */}
              {tirage.tirageDescriptionNumeroEnregistrement && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionNumeroEnregistrementLabelFront") + " "}
                  {tirage.tirageDescriptionNumeroEnregistrement}
                </p>
              )}

              {/* Inventory number */}
              {tirage.tirageDescriptionNumeroInventaire && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionNumeroInventaireLabelFront") + " "}
                  {tirage.tirageDescriptionNumeroInventaire}
                </p>
              )}

              {/* Date of creation */}
              {tirage.tirageDescriptionDateCreation && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionDateCreationLabelFront") + " "}
                  {tirage.tirageDescriptionDateCreation}
                </p>
              )}

              {/* Type */}
              {getMultilingualField(tirage.tirageDescriptionType) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionTypeLabelFront") + " "}
                  {getMultilingualField(tirage.tirageDescriptionType)}
                </p>
              )}

              {/* Printer */}
              {getMultilingualField(tirage.tirageDescriptionPrinter) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionPrinterLabelFront") + " "}
                  {getMultilingualField(tirage.tirageDescriptionPrinter)}
                </p>
              )}

              {/* Technique and Support */}
              {(tirage.tirageDescriptionTechnique?.[locale] ||
                tirage.tirageDescriptionSupport?.[locale]) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionTechniqueSupportLabelFront") + " "}
                  <SupportTechniqueCF
                    input={{
                      technique: tirage.tirageDescriptionTechnique || {},
                      support: tirage.tirageDescriptionSupport || {},
                      preposition: t(
                        "computeField.techniqueSupportPreposition",
                      ),
                    }}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {/* Edition number */}
              {getMultilingualField(tirage.tirageDescriptionEditionNumber) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionEditionNumberLabelFront") + " "}
                  {getMultilingualField(tirage.tirageDescriptionEditionNumber)}
                </p>
              )}

              {/* Signature/Inscription */}
              {getMultilingualField(
                tirage.tirageDescriptionSignatureInscription,
              ) && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionSignatureInscriptionLabelFront") + " "}
                  {getMultilingualField(
                    tirage.tirageDescriptionSignatureInscription,
                  )}
                </p>
              )}

              {/* Description de la signature du dos */}
              {getMultilingualField(
                tirage.tirageDescriptionSignatureInscriptionDos,
              ) &&
                tirage.tirageDescriptionSignatureInscriptionDosPublished?.[
                  locale
                ] && (
                  <p className="font-normal text-sm text-center w-full">
                    {t("tirageDescriptionSignatureInscriptionDosLabelFront") +
                      " "}
                    {getMultilingualField(
                      tirage.tirageDescriptionSignatureInscriptionDos,
                    )}
                  </p>
                )}

              {/* Dimensions with margins */}
              {hasDimensionsWithMargins && dimensionsWithMargins && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionDimensionsWithMarginsLabelFront") + " "}
                  <DimensionsFormatting
                    input={dimensionsWithMargins}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {/* Dimensions without margins */}
              {hasDimensionsWithoutMargins && dimensionsWithoutMargins && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionDimensionsWithoutMarginsLabelFront") +
                    " "}
                  <DimensionsFormatting
                    input={dimensionsWithoutMargins}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {/* Collector */}
              {tirage.collector && typeof tirage.collector === "object" && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionCollectorLabelFront") + " "}
                  {computeFieldCollectorNameForOverview(
                    {
                      collectionneursDescriptionNom:
                        tirage.collector.collectionneursDescriptionNom,
                      collectionneursDescriptionPrenom:
                        tirage.collector.collectionneursDescriptionPrenom,
                      collectionneursDescriptionCollectionPublique:
                        tirage.collector
                          .collectionneursDescriptionCollectionPublique,
                      collectionneursDescriptionVille:
                        tirage.collector.collectionneursDescriptionVille,
                      collectionneursDescriptionVillePublished:
                        tirage.collector.collectionneursDescriptionVillePublished,
                      collectionneursDescriptionPays:
                        tirage.collector.collectionneursDescriptionPays,
                      collectionneursDescriptionPaysPublished:
                        tirage.collector.collectionneursDescriptionPaysPublished,
                      showCity: true,
                    },
                    { language: locale, translation: t },
                  )}
                </p>
              )}

              {/* Credit */}
              {displayData.oeuvresCreditsCreditOeuvre && (
                <p className="font-normal text-sm text-center w-full">
                  {t("tirageDescriptionCreditLabelFront") + " "}
                  {displayData.oeuvresCreditsCreditOeuvre}
                </p>
              )}

              {tirage.tirageRemarquesPubliquesPublished === true &&
                tirage.tirageRemarquesPubliquesMultilingual?.[locale] && (
                  <div className="my-4 flex flex-col gap-2 font-medium text-sm text-center w-full">
                    {t("singleArtworkSubtitlePublicNote")}
                    <ReadMoreHtml
                      className="font-normal text-sm"
                      locale={locale}
                      html={tirage.tirageRemarquesPubliquesMultilingual[locale]}
                    />
                  </div>
                )}

              {/* Accordions */}
              {tirageHistoryCalculated ||
              (tirage.tirageInExhibition &&
                tirage.tirageInExhibition.length > 0) ||
              (tirage.tirageInBibliography &&
                tirage.tirageInBibliography.length > 0) ||
              (!provenanceMode && publishedTirageInAuction.length > 0) ? (
                <div className="w-3/4 mt-6 mb-8">
                  <Accordion
                    type="multiple"
                    className="font-normal text-base text-gray-800"
                  >
                    {tirageHistoryCalculated && (
                      <HistoryAccordion
                        historyCalculated={null}
                        tirageHistoryCalculated={tirageHistoryCalculated}
                        t={t}
                        title={
                          provenanceMode
                            ? t("tirage.accordion.provenance.title")
                            : t("tirage.accordion.history.title")
                        }
                      />
                    )}
                    {tirage.tirageInExhibition &&
                      tirage.tirageInExhibition.length > 0 &&
                      tirageExhibitionsCalculated && (
                        <ExhibitionsAccordion
                          exhibitionsCalculated={tirageExhibitionsCalculated}
                          t={t}
                        />
                      )}
                    {tirage.tirageInBibliography &&
                      tirage.tirageInBibliography.length > 0 &&
                      tirageBibliographyCalculated && (
                        <BibliographyAccordion
                          bibliographyCalculated={tirageBibliographyCalculated}
                          t={t}
                        />
                      )}
                    {!provenanceMode &&
                      publishedTirageInAuction.length > 0 &&
                      tirageAuctionsCalculated && (
                        <AuctionsAccordion
                          auctionCalculated={tirageAuctionsCalculated}
                          t={t}
                        />
                      )}
                  </Accordion>
                </div>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
